import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { AddOnType, CheckoutErrorResponseCode, ItemType } from "lib/types/checkout";
import { useMemo } from "react";

export const useOrderSummary = () => {
  const {
    state: { checkoutItems, errorResponses, appliedCartAddOns, availableAddOns },
  } = useCheckoutContext();

  const unservicableError = useMemo(
    () =>
      errorResponses?.find((error) =>
        [CheckoutErrorResponseCode.UARC_008, CheckoutErrorResponseCode.UARC_009].includes(error.code),
      ),
    [errorResponses],
  );

  const metadata = unservicableError?.metadata;

  const { servicableItems, unservicableItems } = useMemo(() => {
    if (!metadata) return { servicableItems: checkoutItems, unservicableItems: [] };

    const value = checkoutItems.reduce(
      (acc, item) => {
        if (metadata?.items?.some((innerItem) => innerItem.item_id === item.variant_id)) {
          acc.unservicableItems.push(item);
        } else if (
          item.has_components &&
          metadata?.items?.some((innerItem) =>
            item.components.some((component) => component.item_id === innerItem.item_id),
          )
        ) {
          acc.unservicableItems.push(item);
        } else {
          acc.servicableItems.push(item);
        }
        return acc;
      },
      { servicableItems: [] as ItemType[], unservicableItems: [] as ItemType[] },
    );
    return value;
  }, [checkoutItems, metadata]);

  const unservicableAddOnsCount = useMemo(() => {
    if (!metadata?.items?.length) return 0;
    // An applied add-on's own line (cart_addons / item_addons entries) doesn't
    // reliably carry the underlying product's variant id - the only place that's
    // guaranteed is the availableAddOns metadata this add-on was selected from.
    const isAddOnUnservicable = (addon: AddOnType) => {
      const addOnVariantId = availableAddOns?.[addon.addon_id]?.variant_id;
      return Boolean(addOnVariantId) && metadata.items!.some((innerItem) => innerItem.item_id === addOnVariantId);
    };
    const unservicableCartAddOnsCount = appliedCartAddOns?.filter(isAddOnUnservicable).length ?? 0;
    const unservicableItemAddOnsCount = checkoutItems.reduce(
      (count, item) => count + (item.appliedItemAddOns?.filter(isAddOnUnservicable).length ?? 0),
      0,
    );
    return unservicableCartAddOnsCount + unservicableItemAddOnsCount;
  }, [appliedCartAddOns, checkoutItems, availableAddOns, metadata]);

  return {
    servicableItems,
    unservicableItems,
    unservicableAddOnsCount,
  };
};
